import { computed, debug, GeorgeFitAPI, LoginAPI, NetbankingAPI, observable } from "aaa";
import { AliasStore } from "components/alias/AliasStore";
import { action } from "mobx";
import { LoginAPIClient, NetbankingAPIClient } from "network/APIClient";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { amountToNumber, numberToEuroAmount, ZERO_AMOUNT, IAmount } from "types/amount";
import { ICardTanCard, IPutCardTanCardsResponse, IPostOrderNewCardTanReaderResponse } from "types/card";
import { IActivateGeorgeIdFromCardTanResponse } from "types/ebvSecapp";
import { IGeorgeFitAccount } from "types/georgeFit";
import { IAuthorizationLimits, IAuthorizationLimitsResponse, IPutAuthorizationLimitsResponse } from "types/settings";
import { ISignInfoResponse } from "types/signing";
import { Rsa } from "util/rsa";
import { ContactsStore } from "stores/ContactsStore";
import { OffersStore } from "stores/OffersStore";

export class CardTanSettingsStore {
    aliasStore = new AliasStore();
    contactsStore = new ContactsStore();
    offersStore = new OffersStore();

    @observable changePinResponse?: ISignInfoResponse;
    @observable oldPin = "";
    @observable newPin = "";
    @observable newPinRepeat = "";

    @observable selectedLimitOption?: "noLimit" | "setLimit";
    @observable limitResponse?: IPutAuthorizationLimitsResponse;
    @observable limits?: IAuthorizationLimitsResponse;
    @observable cardTanLimit?: IAuthorizationLimits;
    @observable withoutLimit = true;
    @observable limit: number = 0;
    @observable originalLimit: number = 0;
    @observable isLoadingLimits = false;

    @observable isLoadingDebitCards = false;
    @observable isLoadingAccounts = false;
    @observable debitCards: ICardTanCard[] = [];
    @observable updatedDebitCards: ICardTanCard[] = [];
    @observable accounts?: IGeorgeFitAccount[];
    @observable updateDebitCardsResponse?: IPutCardTanCardsResponse;

    @observable activateGeorgeIdResponse?: IActivateGeorgeIdFromCardTanResponse;

    @observable cardTanReaderUnitPrice?: IAmount;
    @observable orderNewCardTanReaderResponse?: IPostOrderNewCardTanReaderResponse;

    @observable isDeletingDevice = false;
    @observable _isLoading = false;
    @observable isLoadingCardTanReaderOfferAndPrice = false;
    @observable isLoadingCardTanReaderOfferFinished = false;
    @observable dataLoadingFinished = false;

    @computed get isLoading() {
        return this._isLoading
            || this.isLoadingLimits
            || this.isLoadingDebitCards
            || this.isLoadingAccounts
            || this.aliasStore.isLoading
            || this.contactsStore.isLoading
            || externalInterfaceStore.isLoadingSettings;
    }

    @action async loadData() {
        try {
            this._isLoading = true;

            await Promise.all([
                this.loadSettings(),
                this.loadLimits(),
                this.loadDebitCards(),
                this.contactsStore.load(undefined, "AT")
            ]);

            this._isLoading = false;

            // prevents reloading offers and state change warning when site is refreshed for new device
            this.dataLoadingFinished = true;
        } catch (error) {
            debug.log("[CardTanSettingsStore] loadData error:", error);

            throw error;
        }
    }

    @action async loadSettings() {
        if (externalInterfaceStore.settings || externalInterfaceStore.isLoadingSettings) {
            // We've already loaded the settings -> get out
            return;
        }

        await externalInterfaceStore.loadSettings();
    }

    @action async loadLimits() {
        const target = NetbankingAPI.getAuthorizationLimits();
        this.isLoadingLimits = true;

        try {
            this.limits = await NetbankingAPIClient.requestType(target);
            this.cardTanLimit = this.limits.limits.find(limit => limit.authorizationType === "CARDTAN");
            if (!this.cardTanLimit) {
                throw new Error("No CARDTAN limit found");
            }

            this.limit = amountToNumber(this.cardTanLimit?.dailyLimit ?? ZERO_AMOUNT);
            this.originalLimit = this.limit;
            this.withoutLimit = this.limit === 0;

            debug.log("### limits", this.limits, this.cardTanLimit);

            this.isLoadingLimits = false;
        } catch (error) {
            this.isLoadingLimits = false;
            throw error;
        }
    }

    @action restoreOriginalLimit() {
        this.limit = this.originalLimit;
        this.withoutLimit = this.limit === 0;
    }

    @action async changeLimit() {
        if (!this.cardTanLimit) {
            return;
        }

        this.cardTanLimit.dailyLimit = this.withoutLimit === false ? numberToEuroAmount(this.limit) : undefined;

        const target = NetbankingAPI.putAuthorizationLimits(this.cardTanLimit);

        try {
            this.limitResponse = await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        }
    }

    resetPinValues() {
        this.oldPin = "";
        this.newPin = "";
        this.newPinRepeat = "";
    }

    @action async changePin() {
        try {
            const target = LoginAPI.getPinEncryptionDetails();
            const pinEncryptionDetails = await LoginAPIClient.requestType(target);

            const rsa = Rsa.getKey();
            rsa.setPublic(pinEncryptionDetails.modulus, pinEncryptionDetails.exponent);
            const payload = rsa.encrypt([pinEncryptionDetails.salt, this.oldPin, this.newPin].join("\t"));

            const putTarget = NetbankingAPI.putCardTanPin(payload);
            this.changePinResponse = await NetbankingAPIClient.requestType(putTarget);
        } catch (error) {
            throw error;
        }
    }

    @action async loadDebitCards() {
        const target = NetbankingAPI.getCardTanDebitCards();

        this.isLoadingDebitCards = true;

        try {
            const debitCardsResponse = await NetbankingAPIClient.requestType(target);
            this.debitCards = debitCardsResponse.cardTanCards;
            this.updatedDebitCards = this.debitCards;

            debug.log("### debitCards", this.debitCards);
        } catch (error) {
            throw error;
        } finally {
            this.isLoadingDebitCards = false;
        }
    }

    @action async loadAccounts() {
        const target = GeorgeFitAPI.getAccounts();

        this.isLoadingAccounts = true;

        try {
            const accountsResponse = await NetbankingAPIClient.requestType(target);
            this.accounts = accountsResponse;

            debug.log("### accounts", this.debitCards);
        } catch (error) {
            throw error;
        } finally {
            this.isLoadingAccounts = false;
        }
    }

    @action async loadCardTanReaderOfferAndPrice() {
        this.isLoadingCardTanReaderOfferAndPrice = true;

        try {
            await this.offersStore.load();

            const cardTanReaderOffer = this.offersStore.findByChannelProduct("CARDTAN-READER");

            if (cardTanReaderOffer) {
                const target = NetbankingAPI.getOfferDetails(cardTanReaderOffer);
                const response = await NetbankingAPIClient.requestType(target);

                this.cardTanReaderUnitPrice = response.unitPrice;
            }

            this.isLoadingCardTanReaderOfferFinished = true;
        } catch (error) {
            throw error;
        } finally {
            this.isLoadingCardTanReaderOfferAndPrice = false;
        }
    }

    @action async orderNewDevice() {
        if (!this.cardTanReaderUnitPrice || !this.contactsStore.selectedEmailAddressId || !this.contactsStore.selectedAddressId) {
            return;
        }

        this._isLoading = true;

        try {
            const target = NetbankingAPI.postOrderNewCardTanDevice({
                unitPrice: this.cardTanReaderUnitPrice,
                emailContactId: this.contactsStore.selectedEmailAddressId,
                recipientAddressId: this.contactsStore.selectedAddressId,
                numberOfReaders: 1
            });

            this.orderNewCardTanReaderResponse = await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        } finally {
            this._isLoading = false;
        }
    }

    @action async updateDebitCards() {
        try {
            const target = NetbankingAPI.putCardTanDebitCards({ cardTanCards: this.updatedDebitCards });
            this.updateDebitCardsResponse = await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        }
    }

    @action async activateGeorgeId() {
        if (!this.cardTanLimit?.dailyLimit) {
            return;
        }

        try {
            const target = NetbankingAPI.activateGeorgeIdFromCardTan({ gidAppDailyLimit: this.cardTanLimit?.dailyLimit });
            this.activateGeorgeIdResponse = await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/loginManagement/cardTanSettings/CardTanSettingsStore.ts